import React from 'react';
import { Stethoscope, BarChart3 } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

export type WorkspaceMode = 'clinical' | 'operations';

interface WorkspaceToggleProps {
  mode: WorkspaceMode;
  onChange: (mode: WorkspaceMode) => void;
  isCollapsed: boolean;
}

const WorkspaceToggle: React.FC<WorkspaceToggleProps> = ({ mode, onChange, isCollapsed }) => {
  if (isCollapsed) {
    return (
      <div className="flex-none border-b border-sidebar-border px-2 py-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => onChange(mode === 'clinical' ? 'operations' : 'clinical')}
              className="flex w-full items-center justify-center rounded-md p-2 transition-colors hover:bg-sidebar-accent"
            >
              {mode === 'clinical' ? (
                <Stethoscope className="h-4 w-4 text-brand-teal" strokeWidth={2} />
              ) : (
                <BarChart3 className="h-4 w-4 text-violet-400" strokeWidth={2} />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-slate-900 text-white border-slate-800">
            <p className="font-medium">{mode === 'clinical' ? 'Clinical View' : 'Operations View'}</p>
            <p className="text-xs text-slate-400">Click to switch</p>
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className={`flex-none border-b px-3 py-2.5 transition-colors duration-200 ${mode === 'operations' ? 'border-violet-500/30' : 'border-sidebar-border'}`}>
      <div className="flex items-center gap-1.5 rounded-lg bg-sidebar-accent/60 p-1">
        <button
          onClick={() => onChange('clinical')}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] font-semibold transition-all duration-200
            ${mode === 'clinical'
              ? 'bg-sidebar text-sidebar-foreground shadow-sm'
              : 'text-sidebar-foreground/50 hover:text-sidebar-foreground/70'
            }`}
        >
          <Stethoscope className={`h-3 w-3 ${mode === 'clinical' ? 'text-brand-teal' : 'text-sidebar-foreground/40'}`} strokeWidth={2.5} />
          Clinical
        </button>
        <button
          onClick={() => onChange('operations')}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[11px] font-semibold transition-all duration-200
            ${mode === 'operations'
              ? 'bg-violet-600/20 text-violet-300 shadow-sm ring-1 ring-violet-500/30'
              : 'text-sidebar-foreground/50 hover:text-sidebar-foreground/70'
            }`}
        >
          <BarChart3 className={`h-3 w-3 ${mode === 'operations' ? 'text-violet-400' : 'text-sidebar-foreground/40'}`} strokeWidth={2.5} />
          Operations
        </button>
      </div>
    </div>
  );
};

export default WorkspaceToggle;
